import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  DocumentHistoryItem,
  DocumentTab,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import {
  allDocumentsHaveRevisionCodes,
  getLatestDocumentByRevision,
} from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import { useCallback } from 'react'
import { DocumentViewerAssetType, SystemDocumentEntry } from 'types'

import { useOcrRefsCount } from './useOcrRefsCount'
import { OcrResultWithPage, useOcrResults } from './useOcrResults'

export type LinkedDocumentsNavigationParams = {
  ocrResult: OcrResultWithPage
  forceNewTab: boolean
}

export type LinkedDocumentsNavigationOptions = {
  offPageConnectorDocuments?: SystemDocumentEntry[]
  isLoadingOffPageConnector?: boolean
}

export const useLinkedDocumentsNavigation = (
  options: LinkedDocumentsNavigationOptions
) => {
  const {
    activeTabId,
    setPagination: setGlobalPagination,
    pushToHistory,
    addDocumentTab,
    addTab,
  } = useKnowledgeDocumentUXContext()

  const { filteredOcrResults } = useOcrResults()
  const { ocrResultRefsMap, getLinkedDocumentsForResult, isLoadingRefs } =
    useOcrRefsCount(filteredOcrResults)

  const navigateOrOpenDocument = useCallback(
    (documentId: string, forceNewTab: boolean) => {
      if (forceNewTab) {
        addDocumentTab({ documentId, pageNumber: 1, selectedBoxIds: [] })
      } else {
        const newDocItem: DocumentHistoryItem = {
          type: 'document',
          documentId,
          pageNumber: 1,
          selectedBoxIds: [],
          query: '',
        }
        pushToHistory(activeTabId, newDocItem)
      }
    },
    [addDocumentTab, pushToHistory, activeTabId]
  )

  const showLinkedDocsTable = useCallback(
    (documentIds: string[], referenceText: string, forceNewTab: boolean) => {
      setGlobalPagination({ pageIndex: 0, pageSize: 200 })
      const linkedDocsItem: SearchHistoryItem = {
        type: 'search',
        searchState: {
          searchMode: 'keyword',
          query: '',
          semanticQuery: '',
          filters: [],
          sorting: [{ id: 'documentTitle', desc: false }],
          pagination: { pageIndex: 0, pageSize: 200 },
          selectedDocumentId: null,
        },
        linkedDocuments: {
          referenceText,
          documentIds,
        },
      }

      if (forceNewTab) {
        const newTab: DocumentTab = {
          id: `linked-docs-${Date.now()}`,
          history: [linkedDocsItem],
          currentIndex: 0,
        }
        addTab(newTab)
      } else {
        pushToHistory(activeTabId, linkedDocsItem)
      }
    },
    [setGlobalPagination, pushToHistory, activeTabId, addTab]
  )

  /**
   * Navigates to linked documents for a given OCR result.
   * Returns true if navigation was handled, false otherwise.
   */
  const navigateToLinkedDocuments = useCallback(
    ({ ocrResult, forceNewTab }: LinkedDocumentsNavigationParams): boolean => {
      if (ocrResult.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
        if (options?.isLoadingOffPageConnector) return false
        const linkedDocuments = options?.offPageConnectorDocuments ?? []

        if (linkedDocuments.length === 0) return false

        if (linkedDocuments.length > 1) {
          if (allDocumentsHaveRevisionCodes(linkedDocuments)) {
            const latestDocument = getLatestDocumentByRevision(linkedDocuments)
            if (!latestDocument) return false
            navigateOrOpenDocument(latestDocument.entry.id, forceNewTab)
            return true
          }

          // Multiple documents without revision codes - show in table
          const documentIds = linkedDocuments.map((doc) => doc.entry.id)
          showLinkedDocsTable(documentIds, ocrResult.text ?? '', forceNewTab)
          return true
        }

        // Single linked document - navigate directly
        navigateOrOpenDocument(linkedDocuments[0].entry.id, forceNewTab)
        return true
      }

      // Handle regular linked documents (from refs)
      const linkedDocs = getLinkedDocumentsForResult(ocrResult)
      if (!linkedDocs.length) return false

      const linkedSystemDocumentIds = [
        ...new Set(linkedDocs.map((doc) => doc.entry.tags['system-document'])),
      ].filter(Boolean)

      if (linkedSystemDocumentIds.length === 0) return false

      // If only one reference, navigate directly to it
      if (linkedSystemDocumentIds.length === 1) {
        navigateOrOpenDocument(linkedSystemDocumentIds[0], forceNewTab)
        return true
      }

      // Multiple references - show in table
      showLinkedDocsTable(
        linkedSystemDocumentIds,
        ocrResult.text ?? '',
        forceNewTab
      )
      return true
    },
    [
      getLinkedDocumentsForResult,
      navigateOrOpenDocument,
      showLinkedDocsTable,
      options?.offPageConnectorDocuments,
      options?.isLoadingOffPageConnector,
    ]
  )

  const hasLinkedDocuments = useCallback(
    (ocrResult: OcrResultWithPage): boolean => {
      const key = `${ocrResult.id}-${ocrResult.pageNumber}`
      const refsCount = ocrResultRefsMap.get(key) ?? 0

      if (refsCount > 0) return true
      if (ocrResult.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
        if (options?.isLoadingOffPageConnector) return false
        const linkedDocuments = options?.offPageConnectorDocuments ?? []
        return linkedDocuments.length > 0
      }

      return false
    },
    [
      ocrResultRefsMap,
      options?.offPageConnectorDocuments,
      options?.isLoadingOffPageConnector,
    ]
  )

  return {
    navigateToLinkedDocuments,
    hasLinkedDocuments,
    ocrResultRefsMap,
    getLinkedDocumentsForResult,
    navigateOrOpenDocument,
    showLinkedDocsTable,
    isLoadingRefs,
  }
}
